import {
  labelFromBotFilename,
  migrateBotProfile,
  type BotProfileDocument,
} from '../../sim/botProfile/index.ts';
import { isSafeBotFilename } from '../../sim/botProfile/filename.ts';

export type BotCatalogEntry = {
  id: string;
  label: string;
  profile: BotProfileDocument;
};

export const BOT_CATALOG_PATH = '/__battleboats/bots';

const botModules = import.meta.glob('/bots/*.json', {
  eager: true,
  import: 'default',
}) as Record<string, unknown>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function idFromPath(path: string): string {
  return path.replace(/^\/bots\//, '').replace(/\.json$/i, '');
}

/** Preview/production catalog from the Vite glob. DEV prefers a live disk listing. */
export function buildBotCatalog(modules: Record<string, unknown>): BotCatalogEntry[] {
  const entries: BotCatalogEntry[] = [];

  for (const [path, data] of Object.entries(modules)) {
    const profile = migrateBotProfile(data);

    if (!profile) {
      console.warn(`Skipping ${path}: not a valid Battleboats bot profile.`);
      continue;
    }

    const id = idFromPath(path);
    entries.push({
      id,
      label: profile.name.trim() || labelFromBotFilename(id),
      profile,
    });
  }

  return entries.sort((a, b) => a.label.localeCompare(b.label));
}

export function botCatalogFromRepoPayload(value: unknown): BotCatalogEntry[] | null {
  if (!isRecord(value) || !Array.isArray(value.bots)) {
    return null;
  }

  const modules: Record<string, unknown> = {};

  for (const entry of value.bots) {
    if (!isRecord(entry) || typeof entry.id !== 'string' || !isSafeBotFilename(`${entry.id}.json`)) {
      continue;
    }

    modules[`/bots/${entry.id}.json`] = entry.data;
  }

  return buildBotCatalog(modules);
}

const bundledBotCatalog: BotCatalogEntry[] = buildBotCatalog(botModules);

export async function fetchBotCatalogFromRepo(): Promise<BotCatalogEntry[] | null> {
  if (!import.meta.env.DEV) {
    return null;
  }

  try {
    const response = await fetch(BOT_CATALOG_PATH, { cache: 'no-store' });

    if (!response.ok) {
      return null;
    }

    return botCatalogFromRepoPayload(await response.json());
  } catch {
    return null;
  }
}

/** DEV reads `bots/` from disk. Preview and production use the files bundled at build time. */
export async function loadBotCatalog(): Promise<readonly BotCatalogEntry[]> {
  return (await fetchBotCatalogFromRepo()) ?? bundledBotCatalog;
}

export function findBotCatalogEntry(
  id: string,
  catalog: readonly BotCatalogEntry[],
): BotCatalogEntry | undefined {
  return catalog.find((entry) => entry.id === id);
}
